import React, { useEffect, useRef, useState } from "react";
import jsQR from "jsqr";
import { Camera, CameraOff, Image as ImageIcon, Keyboard, Loader2 } from "lucide-react";

const decodeImage = (img) => {
  const c = document.createElement("canvas");
  const scale = Math.min(1, 1200 / Math.max(img.naturalWidth || img.width, img.naturalHeight || img.height));
  c.width = Math.round((img.naturalWidth || img.width) * scale); c.height = Math.round((img.naturalHeight || img.height) * scale);
  const ctx = c.getContext("2d"); ctx.drawImage(img, 0, 0, c.width, c.height);
  const d = ctx.getImageData(0, 0, c.width, c.height);
  return jsQR(d.data, d.width, d.height, { inversionAttempts: "attemptBoth" });
};

export const QrScanner = ({ onCode, tr }) => {
  const videoRef = useRef(); const canvasRef = useRef(); const raf = useRef(); const stream = useRef();
  const [cam, setCam] = useState("idle"); // idle | starting | live | denied
  const [manual, setManual] = useState("");
  const [busy, setBusy] = useState(false);

  const stop = () => { cancelAnimationFrame(raf.current); stream.current?.getTracks().forEach((t) => t.stop()); stream.current = null; setCam("idle"); };
  useEffect(() => () => stop(), []);

  const tick = () => {
    const v = videoRef.current, c = canvasRef.current;
    if (v && c && v.readyState === 4) {
      c.width = v.videoWidth; c.height = v.videoHeight;
      const ctx = c.getContext("2d"); ctx.drawImage(v, 0, 0, c.width, c.height);
      const d = ctx.getImageData(0, 0, c.width, c.height);
      const q = jsQR(d.data, d.width, d.height, { inversionAttempts: "dontInvert" });
      if (q?.data) { stop(); onCode(q.data, "camera"); return; }
    }
    raf.current = requestAnimationFrame(tick);
  };
  const start = async () => {
    setCam("starting");
    try {
      stream.current = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: "environment" } }, audio: false });
      videoRef.current.srcObject = stream.current; await videoRef.current.play(); setCam("live"); raf.current = requestAnimationFrame(tick);
    } catch { setCam("denied"); }
  };
  const onFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return; setBusy(true);
    const img = new Image(); const url = URL.createObjectURL(f);
    img.onload = () => { const q = decodeImage(img); URL.revokeObjectURL(url); setBusy(false); q?.data ? onCode(q.data, "upload") : onCode(null, "upload"); };
    img.onerror = () => { setBusy(false); onCode(null, "upload"); };
    img.src = url; e.target.value = "";
  };
  return (
    <div className="space-y-3" data-testid="qr-scanner">
      <div className="relative aspect-square max-h-[60vh] w-full bg-black border border-white/10 overflow-hidden">
        <video ref={videoRef} className={`absolute inset-0 h-full w-full object-cover ${cam === "live" ? "" : "hidden"}`} playsInline muted data-testid="qr-video" />
        <canvas ref={canvasRef} className="hidden" />
        {cam === "live" && <div className="absolute inset-[14%] border-2 border-[#00FF7F]/80 qs-scan-frame pointer-events-none" />}
        {cam !== "live" && (
          <div className="absolute inset-0 grid place-items-center text-center p-6">
            <div>
              {cam === "denied" ? <CameraOff className="h-10 w-10 mx-auto text-[#EF4444]" /> : <Camera className="h-10 w-10 mx-auto text-[#00FF7F]" />}
              <p className="text-sm text-slate-300 mt-3">{cam === "denied" ? (tr ? "Kameraya erişilemedi — kodu elle girin veya QR fotoğrafı yükleyin." : "Camera unavailable — type the code or upload a QR photo.") : (tr ? "Tezgâhtaki QR'ı kameraya tutun" : "Point the camera at the QR on the pack or stall label")}</p>
              <button data-testid="qr-start-camera" onClick={start} disabled={cam === "starting"} className="qs-cta mt-4 bg-[#00FF7F] text-[#050A15] font-semibold px-5 py-2.5 text-sm inline-flex items-center gap-2 disabled:opacity-50">{cam === "starting" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />} {tr ? "Kamerayı aç" : "Open camera"}</button>
            </div>
          </div>)}
        {cam === "live" && <button data-testid="qr-stop-camera" onClick={stop} className="absolute top-2 right-2 bg-black/60 border border-white/20 text-xs text-slate-200 px-2 py-1">{tr ? "durdur" : "stop"}</button>}
      </div>
      <div className="grid grid-cols-[1fr_auto] gap-2">
        <form onSubmit={(e) => { e.preventDefault(); if (manual.trim()) onCode(manual.trim(), "manual"); }} className="flex gap-1">
          <div className="relative flex-1"><Keyboard className="h-3.5 w-3.5 absolute left-2 top-2.5 text-slate-500" /><input data-testid="qr-manual-input" value={manual} onChange={(e) => setManual(e.target.value)} placeholder={tr ? "kod · QS-XXXXXXXX" : "code · QS-XXXXXXXX"} className="w-full bg-[#050A15] border border-white/10 pl-7 pr-2 py-2 text-sm font-mono text-slate-200 uppercase" /></div>
          <button data-testid="qr-manual-submit" className="border border-[#00FFFF]/60 text-[#00FFFF] px-3 text-xs hover:bg-[#00FFFF]/10">{tr ? "aç" : "open"}</button>
        </form>
        <label className="border border-white/15 text-slate-300 px-3 py-2 text-xs inline-flex items-center gap-1 cursor-pointer hover:border-[#00FF7F]/50" data-testid="qr-upload-label">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImageIcon className="h-3.5 w-3.5" />} {tr ? "fotoğraf" : "photo"}<input data-testid="qr-upload-input" type="file" accept="image/*" capture="environment" className="hidden" onChange={onFile} /></label>
      </div>
    </div>
  );
};
